<template>
  <div class="mt-8">
    <div class="intro-y flex flex-col sm:flex-row items-center flex-wrap gap-4">
      <PageHeader class="text-lg font-medium mr-auto" />
      <div class="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 w-full sm:w-auto">
        <InputDate v-model="capitronFetchDate" :label="$t('table.date')" class="min-w-[200px]" />
        <Button
          variant="primary"
          class="whitespace-nowrap"
          :loading="capitronFetchLoading"
          :disabled="!capitronFetchDate"
          @click="onFetchCapitronStatements"
        >
          {{ $t('journal.fetchBankStatements') }}
        </Button>
      </div>
    </div>
    <div class="intro-y mt-5 overflow-auto lg:overflow-visible">
      <CustomerTransactionsTable
        :loading="isLoading"
        :rows="customerTransactions"
        :columns="columns"
        :line-numbers="false"
        :sort="sort"
        :page="page"
        :per-page="perPage"
        :page-size="pageSize"
        :total-page="totalPage"
        :total-records="totalRecords"
        @onFilter="onFilter"
        @onPageChange="onPageChange"
        @onPerPageChange="onPerPageChange"
        @onSortChange="onSortChange"
        @click="showModal"
      />
    </div>

    <ModalEdit
      :id="modalId"
      :title="$t('text.connectForLoan')"
      :loading="isUpdating"
      @click="onSubmit"
      @onClickCancel="resetForm"
    >
      <div class="p-5">
        <InputString
          :label="$t('text.loanId')"
          v-model.number="$v.form.loanId.$model"
          :vuelidate="$v.form.loanId"
        />
        <InputSelect
          :label="$t('text.status')"
          v-model.number="$v.form.status.$model"
          :options="statusList"
          value-key="id"
          :vuelidate="$v.form.status"
          :first-item-label="$t('text.pleaseSelectAnAction')"
          first-item-disabled
        />
      </div>
    </ModalEdit>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  CUSTOMER_TRANSACTIONS_REQUEST,
  CUSTOMER_TRANSACTION_UPDATE,
  CUSTOMER_TRANSACTION_CAPITRON_FETCH,
} from '@/store/actions/journal';
import dataListMixin from '@/mixins/dataListMixin';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import PageHeader from '@/components/ui/PageHeader';
import CustomerTransactionsTable from '@/components/generalJournal/CustomerTransactionsTable';
import ModalEdit from '@/components/ui/ModalEdit';
import InputString from '@/components/ui/input/InputString';
import InputSelect from '@/components/ui/input/InputSelect';
import InputDate from '@/components/ui/input/InputDate';
import Button from '@/components/ui/button/Button';
import moment from 'moment';

export default {
  mixins: [dataListMixin, validationMixin],

  validations() {
    return {
      form: {
        loanId: {
          required,
        },
        status: {
          required,
        },
      },
    };
  },

  data() {
    return {
      storeActionListRequest: CUSTOMER_TRANSACTIONS_REQUEST,
      columns: [
        {
          label: 'ID',
          field: 'id',
          sortable: true,
          filterable: true,
        },
        {
          label: this.$t('table.date'),
          field: 'transactionDate',
          filterable: true,
          filterType: 'date',
        },
        {
          label: this.$t('table.transactionDescription'),
          field: 'description',
          filterable: true,
        },
        {
          label: this.$t('table.income'),
          field: 'income',
        },
        {
          label: this.$t('table.expense'),
          field: 'expense',
        },
        {
          label: this.$t('table.lastBalance'),
          field: 'lastBalance',
        },
        {
          label: this.$t('table.handlingBankAccount'),
          field: 'fromToAccount',
          filterable: true,
        },
        {
          label: this.$t('table.companyBankAccount'),
          field: 'bankAccount',
          filterable: true,
        },
        {
          label: this.$t('table.loan'),
          field: 'loanId',
        },
        {
          label: this.$t('table.status'),
          field: 'status',
        },
      ],
      transactionIdToUpdate: null,
      modalId: 'customer-transaction-update',
      form: {
        loanId: '',
        status: null,
      },
      capitronFetchDate: null,
      statusList: [
        { id: 'LOAN_ISSUED', name: this.$t('status.loanIssued') },
        { id: 'LOAN_PAID', name: this.$t('status.loanPaid') },
        { id: 'LOAN_EXTENDED', name: this.$t('status.loanExtended') },
        { id: 'WRITE_OFF', name: this.$t('status.loanWriteOffVerb') },
      ],
    };
  },

  computed: {
    ...mapGetters([
      'customerTransactions',
      'customerTransactionsMeta',
      'customerTransactionsStatus',
      'customerTransactionUpdateStatus',
      'capitronStatementsStatus',
    ]),
    capitronFetchLoading() {
      return this.capitronStatementsStatus === 'loading';
    },
    isLoading() {
      return this.customerTransactionsStatus === 'loading';
    },
    isUpdating() {
      return false;
    },
  },

  created() {
    this.capitronFetchDate = moment().format('YYYY-MM-DD');
    this.fetchData();
  },

  methods: {
    setPaginationValues() {
      this.totalRecords = this.customerTransactionsMeta ? this.customerTransactionsMeta.total : 1;
      this.page = this.customerTransactionsMeta.currentPage;
      this.totalPage = this.customerTransactionsMeta.lastPage;
    },
    async showModal(id) {
      this.transactionIdToUpdate = id;
      cash(`#${this.modalId}`).modal('show');
    },
    resetForm() {
      this.transactionIdToUpdate = null;
      this.form = {
        loanId: '',
        status: null,
      };
      this.$v.$reset();
    },
    async onFetchCapitronStatements() {
      if (!this.capitronFetchDate) {
        return;
      }
      try {
        await this.$store.dispatch(CUSTOMER_TRANSACTION_CAPITRON_FETCH, {
          date: this.capitronFetchDate,
        });
        this.$toasted.show(this.$t('toast.success'), { type: 'success' });
        this.fetchData();
      } catch (e) {
        /* handleRequestError */
      }
    },
    async onSubmit() {
      if (this.$v) {
        this.$v.form.$touch();
        if (this.$v.form.$anyError) {
          this.$toasted.show(this.$t('toast.failedForm'), { type: 'error' });
          return;
        }
      }

      const payload = {
        transactionId: this.transactionIdToUpdate,
        data: this.form,
      };
      await this.$store.dispatch(CUSTOMER_TRANSACTION_UPDATE, payload);
      if (this.customerTransactionUpdateStatus === 'success') {
        this.resetForm();
        cash(`#${this.modalId}`).modal('hide');
      }
    },
  },

  components: {
    PageHeader,
    CustomerTransactionsTable,
    ModalEdit,
    InputString,
    InputSelect,
    InputDate,
    Button,
  },
};
</script>
